import * as React from "react";
import { useEffect, useCallback } from "react";
import OutsideClickHandler from "@graffer-inc/react-outside-click-handler";
import { useLocation, useParams, useHistory } from "react-router-dom";
import { Loader } from "../../../organisms/common/loading";
import { TabState } from "../../../../application/service/smart-apply/procedure-detail/tab-state";
import { BaseModal } from "../../../atoms/base-modal";
import { useModal } from "../../../../utils/custom-hooks";
import { PermissionErrorMessages } from "../../../organisms/common/permission-error-messages";
import {
  PORTAL_BASE_URL,
  parseQueryOfLocation,
} from "../../../../utils/url-utils";
import { DetailPage } from "../../../organisms/smart-apply/procedure-detail/basic-info";
import { FormEditPage } from "../../../organisms/smart-apply/procedure-detail/form-edit/form-edit";
import { AuthLevel } from "../../../organisms/smart-apply/procedure-detail/auth-level";
import { PresentationSettingContainer } from "../../../organisms/smart-apply/procedure-detail/presentation-setting";
import {
  ProcedureContextProps,
  ProcedureDetailService,
} from "../../../../application/service/service-provider/procedure-detail-service";
import { ServiceProviderTemplate } from "../../../template/service-provider/default-template";
import { MailCfgContainer } from "../../../organisms/smart-apply/procedure-detail/mail-cfg";
import { QuotaSettingContainer } from "../../../organisms/smart-apply/procedure-detail/quota-setting";
import {
  connectPageService,
  GrafferManagedTag,
} from "@graffer-inc/smart-apply";
import { Dropdown } from "../../../molecules/dropdown";
import { ServiceProviderR } from "../routes";
import { ConfirmModal } from "../../../organisms/common/confirm-modal";
import { GroupShareModal } from "../../../organisms/common/group-share-modal";
import { toast } from "react-toastify";
import { PaymentSetting } from "../../../organisms/smart-apply/procedure-detail/payment-setting";
import { ProcedureStatusTag } from "../../../molecules/procedure-status-tag";
import { ConfirmMatrixCodeModal } from "../../../organisms/smart-apply/modals/confirm-matrix-code-modal";
import { useFeatureAvailability } from "../../../organisms/proc-management/hooks";
import { ProcedureCopyModal } from "../../../organisms/common/procedure-copy-modal";
import { MenuButtons } from "./menu-buttons";
import { PublishSettingModal } from "../../../organisms/common/publish-setting-modal";
import { useFeatureFlag_CustomerDlConfig } from "../../../../feature-flags";
import { TenantProcessResultSettingPage } from "../../../organisms/smart-apply/procedure-detail/tenant-process-result-setting/index";
import { CustomerDlPdfCfgModal } from "../../../organisms/smart-apply/modals/customer-dl-pdf-cfg-modal";
import { PdfOutputConfigurationList } from "./pdf-output-configuration/list";
import { AssignCustomPathModalContainer } from "../../../organisms/smart-apply/modals/assign-custom-path";
import {
  SecondaryButton,
  Tabs,
  WarningBanner,
} from "@graffer-inc/console-design-kit";
import { useRBAC } from "../../../helpers/rbac/use-rbac";
import { useFormUnsaved } from "../../../../utils/use-form-unsaved";
import apiClient from "../../../../application/apis/api-client";
import { ConfirmArchiveModal } from "../../../organisms/smart-apply/modals/confirm-archive-modal";

const ProcedureDetailPage: React.FC<ProcedureContextProps> = ({
  isSubmitting,
  resourceLoadState,
  procedure,
  customPathSettings,
  tabState,
  hasCustomUIDoorPage,
  switchTab,
  onProcedureUpdate,
  onCopyCreate,
  onProcedureDelete,
  resetState,
  orgWithUsingFeature,
  onDraftUpdate,
  orgSlug,
  onGroupAdded,
  onExportProcedure,
  onGrafferManagedChanged,
  procMsts,
  onUpdateProcMaster,
  onPublishSettingUpdate,
  fetchProcMstBySmartApply,
  onReturnToDraft,
  onResumeAccepting,
  onPauseAccepting,
  onUnPublish,
  groups,
  onUpdateCustomerDlConfig,
  onArchive,
  onUnarchive,
  isOrgSelfServe,
}) => {
  const { id: orgId, procedureId: id } = useParams<{
    id: string;
    procedureId: string;
  }>();
  const { tab } = parseQueryOfLocation(useLocation());
  const history = useHistory();
  const menuToggle = useModal();
  const manageGroupModal = useModal();
  const deleteConfirmModal = useModal();
  const copyProcedureModal = useModal();
  const assignCustomPathModal = useModal();
  const matrixCodeModal = useModal();
  const exportProcedureModal = useModal();
  const changeGrafferManagedModal = useModal();

  const publishSettingModal = useModal();
  const unpublishConfirmModal = useModal();
  const returnToDraftModal = useModal();
  const pauseAcceptModal = useModal();
  const resumeAcceptModal = useModal();
  const customerDlPdfCfgModal = useModal();
  const archiveConfirmModal = useModal();
  const { setIsFormUnsaved, handleSwitchTab } = useFormUnsaved({
    tabState,
    switchTab,
    object: procedure,
    product: "SMART_APPLY",
  });

  const onCopyProc = async (newName: string) => {
    try {
      const newProc = await onCopyCreate(newName);
      history.push(
        ServiceProviderR.ProcedureDetail.link({
          orgId: orgId,
          procedureId: newProc.id,
        }),
      );
    } catch (e) {
      throw e;
    }
  };

  const onDownloadProc = async () => {
    const successed = await onExportProcedure(id);
    if (successed) {
      toast.success("手続きをダウンロードしました。");
      exportProcedureModal.close();
      return;
    }
    toast.error("手続きのダウンロードに失敗しました。");
  };

  useEffect(() => {
    resetState(id, tab as string | undefined, { orgIdFromPath: orgId });
  }, [resetState, id, tab, orgId]);

  const useProcMst = useFeatureAvailability(orgId).status === "AVAILABLE";
  useEffect(() => {
    if (useProcMst && procedure) {
      fetchProcMstBySmartApply(procedure.id, procedure.orgId);
    }
  }, [fetchProcMstBySmartApply, useProcMst, procedure]);

  // 二次元コード生成画面遷移処理
  const openMatrixCodeWindow = useCallback(async () => {
    if (procedure) {
      await window.open(
        `/service-provider/graffer-utility-tools/matrix-code?queryUrl=${encodeURIComponent(
          procedure.doorPageURL(orgSlug, customPathSettings.currentPath),
        )}`,
        "_blank",
      );
    }
  }, [orgSlug, procedure, customPathSettings.currentPath]);

  const canShowPreview: boolean =
    !!procedure && !procedure.isFormAccessRestricted();
  const openPreview = async () => {
    const { data } = await apiClient.generateProcedurePreviewToken(
      procedure!.id,
    );
    const previewLink = procedure!.previewDoorURL(
      PORTAL_BASE_URL!,
      orgSlug,
      data.previewToken,
    );

    window.open(previewLink, "_blank");
  };

  const enableCustomerDlConfigFeature = useFeatureFlag_CustomerDlConfig(orgId);

  const checkPermission = useRBAC();

  const parentBreadcrumb =
    procedure && procedure.isArchived()
      ? {
          to: ServiceProviderR.ArchivedProcedures.link({ id: orgId }),
          title: "アーカイブ済み手続き一覧",
        }
      : {
          to: ServiceProviderR.Procedures.link({ id: orgId }),
          title: "手続き一覧",
        };

  const changeGrafferManagedOpen = useCallback(() => {
    if (procedure?.customTag) {
      toast.error("個別プランは管理を変更できません");
      return;
    }
    if (procedure?.isGrafferManaged && !isOrgSelfServe) {
      toast.error("セルフサーブ以外のプランではテナント管理に変更できません");
      return;
    }
    changeGrafferManagedModal.open();
  }, [changeGrafferManagedModal, procedure, isOrgSelfServe]);

  return (
    <ServiceProviderTemplate
      breadcrumbs={[
        { to: ServiceProviderR.Home.link({}), title: "テナント一覧" },
        parentBreadcrumb,
        {
          to: ServiceProviderR.ProcedureDetail.link({ orgId, procedureId: id }),
          title: id,
        },
      ]}
    >
      {resourceLoadState.matchType({
        LOADING: null,
        LOAD_FAILED: <WarningBanner message="手続きの取得に失敗しました。" />,
        NO_PERMISSION: (
          <>
            <div className="columns is-vcentered">
              <div className="column has-text-left">
                <h1 className="title">
                  <i className="fa fa-tachometer" /> 手続き詳細： {id}
                </h1>
              </div>
            </div>
            <div className="columns is-vcentered">
              <div className="column has-text-left is-6">
                <PermissionErrorMessages
                  message={"この手続の詳細を表示する権限がありません"}
                />
              </div>
            </div>
          </>
        ),
        HAS_PERMISSION: (
          <>
            {!!procedure && !!procedure.draft && (
              <>
                {procedure.isArchived() && (
                  <div className="columns is-size-5 has-text-weight-bold is-centered has-background-warning mt-0 mb-3 p-2">
                    この手続きはアーカイブされています。
                  </div>
                )}
                <div
                  className="columns is-vcentered"
                  style={{ marginBottom: 0 }}
                >
                  <div className="column has-text-left">
                    <h1 className="title">
                      <i className="fa fa-tachometer" />
                      {procedure.name}
                      {procedure.isGrafferManaged && (
                        <>
                          &nbsp; &nbsp;
                          <GrafferManagedTag />
                        </>
                      )}
                    </h1>
                  </div>
                  <div className="column has-text-right is-narrow">
                    <div className="buttons has-text-right is-right">
                      {canShowPreview && (
                        <SecondaryButton
                          text="申請プレビュー"
                          icon="OpenInNewIcon"
                          onClick={openPreview}
                          disabled={!!procedure.unPublishableError()}
                        />
                      )}
                      <OutsideClickHandler onOutsideClick={menuToggle.close}>
                        <Dropdown
                          onClick={menuToggle.open}
                          isActive={menuToggle.isActive}
                          isRight={true}
                          menus={[
                            procedure.isGrafferManaged && {
                              title: "管理グループを設定する",
                              onClick: manageGroupModal.open,
                            },
                            !procedure.isArchived() && {
                              title: "申請開始ページURLを予約する",
                              onClick: assignCustomPathModal.open,
                            },
                            {
                              title: "申請開始ページの二次元コードを生成する",
                              onClick: () => {
                                if (customPathSettings.currentPath) {
                                  openMatrixCodeWindow();
                                } else {
                                  matrixCodeModal.open();
                                }
                              },
                            },
                            {
                              title: `手続きを${
                                procedure.isGrafferManaged
                                  ? "テナント"
                                  : "グラファー"
                              }管理に変更する`,
                              onClick: changeGrafferManagedOpen,
                            },
                            !procedure.isArchived() && {
                              title: "手続きをコピーして新規作成する",
                              disable: !procedure?.isCopiable(),
                              onClick: copyProcedureModal.open,
                            },
                            procedure.isGrafferManaged && {
                              title: "手続きを削除する",
                              disable: !procedure.canDelete(),
                              onClick: deleteConfirmModal.open,
                            },
                            {
                              title: "手続きをエクスポートする",
                              onClick: exportProcedureModal.open,
                            },
                            enableCustomerDlConfigFeature && {
                              title: "申請者へのPDFマッピング提供",
                              onClick: customerDlPdfCfgModal.open,
                            },
                            procedure.publishStatus === "UNPUBLISHED" && {
                              title: "手続きをアーカイブする",
                              onClick: archiveConfirmModal.open,
                            },
                            procedure.isArchived() && {
                              title: "手続きをアーカイブから復元する",
                              onClick: onUnarchive,
                            },
                          ]}
                        />
                      </OutsideClickHandler>
                    </div>
                  </div>
                </div>
                <div className="columns is-vcentered">
                  <div className={"column has-text-left"}>
                    <ProcedureStatusTag status={procedure.publishStatus} />
                  </div>
                  <div className={"column has-text-right is-narrow"}>
                    <MenuButtons
                      orgSlug={orgSlug}
                      procedure={procedure}
                      currentCustomPath={customPathSettings.currentPath}
                      onPublishSettingModalOpen={publishSettingModal.open}
                      onUnPublish={unpublishConfirmModal.open}
                      onReturnToDraft={returnToDraftModal.open}
                      onPauseAccepting={pauseAcceptModal.open}
                      onResumeAccepting={resumeAcceptModal.open}
                    />
                  </div>
                </div>
                <div className="columns">
                  <div className="column">
                    <Tabs
                      tabItems={[
                        {
                          label: "基本情報",
                          isActive: tabState.equals("BASIC"),
                          onClick: () => handleSwitchTab(TabState.initialize()),
                          hidden: false,
                        },
                        {
                          label: "申請フォーム",
                          isActive: tabState.equals("FORM"),
                          onClick: () => handleSwitchTab(TabState.form()),
                          hidden: !checkPermission({
                            requirePermissions: "procedures.read",
                          }),
                        },
                        {
                          label: "決済",
                          isActive: tabState.equals("PAYMENT"),
                          onClick: () => handleSwitchTab(TabState.payment()),
                          hidden:
                            !orgWithUsingFeature ||
                            !orgWithUsingFeature.usePayment ||
                            !checkPermission({ target: procedure }),
                        },
                        {
                          label: "認証",
                          isActive: tabState.equals("AUTH_LEVEL"),
                          onClick: () => handleSwitchTab(TabState.authLevel()),
                          hidden: !checkPermission({
                            requirePermissions: "procedures.read",
                          }),
                        },
                        {
                          label: "通知",
                          isActive: tabState.equals("NOTIFICATION_MAIL"),
                          onClick: () =>
                            handleSwitchTab(TabState.notificationMail()),
                          hidden: !checkPermission({
                            requirePermissions: "procedures.mail_cfg_read",
                          }),
                        },
                        {
                          label: "申請数の上限",
                          isActive: tabState.equals("QUOTA"),
                          onClick: () => handleSwitchTab(TabState.quota()),
                          hidden: !checkPermission({
                            target: procedure,
                            requirePermissions: "procedures.read",
                          }),
                        },
                        {
                          label: "事務処理の設定",
                          isActive: tabState.equals("INTERNAL_WORK"),
                          onClick: () =>
                            handleSwitchTab(TabState.internalWork()),
                          hidden: !checkPermission({
                            requirePermissions: "procedures.read",
                          }),
                        },
                        {
                          label: "帳票レイアウト",
                          isActive: tabState.equals("REPORT_OUTPUT"),
                          onClick: () => switchTab(TabState.reportOutput()),
                          hidden: !checkPermission({
                            requirePermissions: "procedures.read",
                          }),
                        },
                        {
                          label: "カスタムページ",
                          isActive: tabState.equals("CUSTOM_UI"),
                          onClick: () => handleSwitchTab(TabState.customUi()),
                          hidden: !checkPermission({
                            requirePermissions:
                              "procedures.presentation_update",
                          }),
                        },
                      ]}
                    />
                  </div>
                </div>
                <Loader isLoading={isSubmitting}>
                  {tabState.matchType({
                    BASIC: (
                      <DetailPage
                        orgSlug={orgSlug}
                        procedure={procedure}
                        existingCustomPathSettings={customPathSettings}
                        onProcedureUpdate={onProcedureUpdate}
                        isServiceProviderAccount={true}
                        useProcMst={useProcMst}
                        procMsts={procMsts}
                        hasCustomUIDoorPage={hasCustomUIDoorPage}
                        onUpdateProcMaster={onUpdateProcMaster}
                        groups={groups}
                      />
                    ),
                    FORM: (
                      <FormEditPage
                        procedure={procedure}
                        onDraftUpdate={onDraftUpdate}
                        setIsFormUnsaved={setIsFormUnsaved}
                      />
                    ),
                    AUTH_LEVEL: (
                      <AuthLevel
                        procedure={procedure}
                        onDraftUpdate={onDraftUpdate}
                      />
                    ),
                    PAYMENT: (
                      <PaymentSetting
                        procedure={procedure}
                        onDraftUpdate={onDraftUpdate}
                      />
                    ),
                    CUSTOM_UI: (
                      <PresentationSettingContainer procedure={procedure} />
                    ),
                    NOTIFICATION_MAIL: (
                      <MailCfgContainer procedure={procedure} />
                    ),
                    QUOTA: <QuotaSettingContainer procedure={procedure} />,
                    INTERNAL_WORK: (
                      <TenantProcessResultSettingPage
                        procedure={procedure}
                        onDraftUpdate={onDraftUpdate}
                      />
                    ),
                    REPORT_OUTPUT: (
                      <PdfOutputConfigurationList procedure={procedure} />
                    ),
                  })}
                </Loader>
                {manageGroupModal.isActive && (
                  <GroupShareModal
                    onClose={manageGroupModal.close}
                    onAdd={onGroupAdded}
                    tenantId={procedure.orgId}
                    sharedGroupIds={procedure.groupIds}
                  />
                )}
                <BaseModal
                  isActive={deleteConfirmModal.isActive}
                  title="手続きを削除"
                  onClose={deleteConfirmModal.close}
                >
                  <section className="modal-card-body">
                    <div className="is-small">
                      手続きを削除してよろしいでしょうか。
                    </div>
                  </section>
                  <footer className="modal-card-foot">
                    <button
                      className="button is-failure is-fullwidth"
                      onClick={deleteConfirmModal.close}
                    >
                      キャンセル
                    </button>
                    <button
                      className="button is-info is-fullwidth"
                      onClick={async () => {
                        await onProcedureDelete();
                        deleteConfirmModal.close();
                        history.push(
                          ServiceProviderR.Procedures.link({ id: orgId }),
                        );
                      }}
                    >
                      削除する
                    </button>
                  </footer>
                </BaseModal>
                <PublishSettingModal
                  publishSetting={procedure.publishSetting}
                  publishStatus={procedure.publishStatus}
                  isPublished={procedure.isPublished()}
                  isActive={publishSettingModal.isActive}
                  onPublishSettingUpdate={onPublishSettingUpdate}
                  onClose={publishSettingModal.close}
                />
                <BaseModal
                  title={"申請受付の一時停止"}
                  isActive={pauseAcceptModal.isActive}
                  onClose={pauseAcceptModal.close}
                >
                  <section className="modal-card-body">
                    <p>
                      受付を一時停止すると再開するまで申請を受けつけることができなくなります。よろしいですか？
                    </p>
                  </section>
                  <section className="modal-card-foot">
                    <button
                      type="button"
                      className="button is-fullwidth"
                      onClick={pauseAcceptModal.close}
                    >
                      キャンセル
                    </button>
                    <button
                      type="button"
                      className="button is-fullwidth is-danger"
                      onClick={() => {
                        onPauseAccepting(procedure!.id);
                        pauseAcceptModal.close();
                      }}
                    >
                      受付を一時停止する
                    </button>
                  </section>
                </BaseModal>
                <BaseModal
                  title={"申請受付の再開"}
                  isActive={resumeAcceptModal.isActive}
                  onClose={resumeAcceptModal.close}
                >
                  <section className="modal-card-body">
                    <p>受付を再開します。よろしいですか？</p>
                  </section>
                  <section className="modal-card-foot">
                    <button
                      type="button"
                      className="button is-fullwidth"
                      onClick={resumeAcceptModal.close}
                    >
                      キャンセル
                    </button>
                    <button
                      type="button"
                      className="button is-fullwidth is-danger"
                      onClick={() => {
                        onResumeAccepting(procedure!.id);
                        resumeAcceptModal.close();
                      }}
                    >
                      受付を再開する
                    </button>
                  </section>
                </BaseModal>

                <BaseModal
                  title={"手続きの公開終了"}
                  isActive={unpublishConfirmModal.isActive}
                  onClose={unpublishConfirmModal.close}
                >
                  <section className="modal-card-body">
                    <p>
                      公開を終了すると申請を受けつけられなくなります。よろしいですか？
                    </p>
                  </section>
                  <section className="modal-card-foot">
                    <button
                      type="button"
                      className="button is-fullwidth"
                      onClick={unpublishConfirmModal.close}
                    >
                      キャンセル
                    </button>
                    <button
                      type="button"
                      className="button is-fullwidth is-danger"
                      onClick={() => {
                        onUnPublish(procedure!.id);
                        unpublishConfirmModal.close();
                      }}
                    >
                      公開を終了する
                    </button>
                  </section>
                </BaseModal>
                <ConfirmModal
                  isActive={returnToDraftModal.isActive}
                  title="下書きに戻す"
                  body="下書きに戻すとすべての項目の更新が可能になります。"
                  successButtonText="戻す"
                  onSuccess={() => {
                    onReturnToDraft();
                    returnToDraftModal.close();
                  }}
                  onClose={returnToDraftModal.close}
                />
                {procedure && copyProcedureModal.isActive && (
                  <ProcedureCopyModal
                    onCopy={onCopyProc}
                    procedure={procedure}
                    isActive={copyProcedureModal.isActive}
                    onClose={copyProcedureModal.close}
                  />
                )}
                <ConfirmModal
                  isActive={exportProcedureModal.isActive}
                  title="手続きをエクスポート"
                  body="この手続きの設定をJSONファイルとしてエクスポートします。"
                  successButtonText="エクスポート"
                  onSuccess={onDownloadProc}
                  onClose={exportProcedureModal.close}
                />
                {assignCustomPathModal.isActive && (
                  <AssignCustomPathModalContainer
                    onClose={assignCustomPathModal.close}
                    orgSlug={orgSlug}
                    afterReallocate={resetState}
                    procedure={procedure}
                  />
                )}
                <ConfirmMatrixCodeModal
                  isActive={matrixCodeModal.isActive}
                  onCancel={matrixCodeModal.close}
                  onBreak={() => {
                    matrixCodeModal.close();
                    assignCustomPathModal.open();
                  }}
                  onSubmit={() => {
                    matrixCodeModal.close();
                    openMatrixCodeWindow();
                  }}
                />
                <ConfirmModal
                  isActive={changeGrafferManagedModal.isActive}
                  title={`手続きを${
                    procedure.isGrafferManaged ? "テナント" : "グラファー"
                  }管理に変更`}
                  body={`この手続きを${
                    procedure.isGrafferManaged ? "テナント" : "グラファー"
                  }管理に変更しますか？`}
                  successButtonText="変更"
                  onSuccess={async () => {
                    await onGrafferManagedChanged(!procedure.isGrafferManaged);
                    changeGrafferManagedModal.close();
                  }}
                  onClose={changeGrafferManagedModal.close}
                />
                <CustomerDlPdfCfgModal
                  isActive={customerDlPdfCfgModal.isActive}
                  procedure={procedure}
                  onCancel={customerDlPdfCfgModal.close}
                  onSave={(value) => {
                    onUpdateCustomerDlConfig(value);
                    customerDlPdfCfgModal.close();
                  }}
                />
                <ConfirmArchiveModal
                  isActive={archiveConfirmModal.isActive}
                  fromServiceProvider={true}
                  onSubmit={async () => {
                    await onArchive();
                    archiveConfirmModal.close();
                  }}
                  onCancel={archiveConfirmModal.close}
                />
              </>
            )}
          </>
        ),
      })}
    </ServiceProviderTemplate>
  );
};

export const OrganizationProcedureDetailContainer = connectPageService(
  ProcedureDetailPage,
  () => new ProcedureDetailService(),
);
